import queryString from "query-string";

/**
 * The link state a My Feed card, title or events-panel card carries to a listing's public page (GROWTH-7576): where the
 * member came from and which filter the feed showed, so the page's "My Feed" breadcrumb can bring them back to it.
 *
 * @param {Object} filters
 * @param {string|null} [filters.type] the feed's type filter ("profile" | "organization" | "event" | "job"), null = All
 * @param {number|null} [filters.communityId] the sidebar's selected community, null = All communities
 * @param {string|null} [filters.q] the header search's text applied to the feed (`/?q=`, GROWTH-7645); the key is left
 *   out of the state when there is none, so a plain feed's state keeps the shape it had before
 * @returns {{from: "feed", feedType: string|null, feedCommunityId: number|null, feedQuery?: string}}
 */
export const feedReturnState = ({ type = null, communityId = null, q = null } = {}) => {
  const feedQuery = typeof q === "string" ? q.trim() : "";

  return {
    from: "feed",
    feedType: type || null,
    feedCommunityId: Number.isInteger(communityId) ? communityId : null,
    ...(feedQuery !== "" ? { feedQuery } : {})
  };
};

/**
 * Where the "My Feed" breadcrumb goes (GROWTH-7576). Arriving from the feed: back to it with the same search text (the
 * feed's `?q=`, GROWTH-7645), type filter (its `?type=` deep link) and selected community (read by the feed from
 * `feedCommunityId` in the link state). Arriving from anywhere else, or with no state: the feed as it opens by default.
 *
 * @param {Object|null|undefined} locationState the listing page's `location.state`
 * @returns {{to: string, state: Object|undefined}}
 */
export const feedReturnTarget = locationState => {
  if (locationState?.from !== "feed") {
    return { to: "/", state: undefined };
  }

  const params = {};
  const feedQuery = typeof locationState.feedQuery === "string" ? locationState.feedQuery.trim() : "";
  if (feedQuery !== "") params.q = feedQuery;
  if (locationState.feedType) params.type = locationState.feedType;
  const search = Object.keys(params).length > 0 ? `?${queryString.stringify(params)}` : "";
  const state = Number.isInteger(locationState.feedCommunityId) ? { feedCommunityId: locationState.feedCommunityId } : undefined;

  return { to: `/${search}`, state };
};
